// Office admin: customer portal logins. Lists every portal login (which customer,
// email, last sign-in, status) and a form to create a new one for a customer.
// Plain English, zero jargon -- the office manager uses this page. Staff-only: the route
// sits behind the office/admin role gate and the API is staff-cookie guarded. The
// create form wires to POST /api/portal/admin/users; the per-row + banner "Send invite"
// controls wire to POST /api/portal/admin/users/:id/invite (office/admin since Session AFI).
import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { api, ApiError } from "../../lib/api";
import type { PortalAdminUser } from "../../lib/api";
import { RecordPicker } from "../../components/RecordPicker";
import { ConfirmButton } from "../../components/ConfirmButton";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { NoActiveContractConfirm, isNoActiveContractRefusal } from "../../components/NoActiveContractConfirm";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill, toneTextClass } from "../../components/StatusPill";
import { formatDate, formatDateTime } from "../../lib/format";
import { filterRows, useListSearch, type SearchAccessors } from "../../lib/useListSearch";
import { sortRows, useSortableColumn, type SortAccessors } from "../../lib/useSortableColumn";

// "Never" when the customer has not signed in yet (the helpers print "-" for an empty value).
const lastSignIn = (iso: string | null): string => (iso ? formatDateTime(iso) : "Never");

// The list's search and sort (design system Session 5, spec 3.7). The customer's name comes WITH each login from the server
// (Session SC2B), so no customer register is loaded to read it.
const SORT_OPTIONS = [
  { key: "customer", label: "Customer" },
  { key: "email", label: "Email" },
  { key: "name", label: "Name" },
  { key: "lastSignIn", label: "Last sign-in" },
  { key: "status", label: "Status" },
  { key: "created", label: "Created" },
];

// Translate the API's error codes into plain English for the office. Any other
// ApiError (session expired, server error) falls through to the generic message
// rather than surfacing a raw code like "unauthorized" or "Internal Server Error".
function createErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) return "That email already has a Client Portal login.";
    if (err.code === "unknown_customer") return "Please choose a customer from the list.";
    if (err.code === "invalid_request") return "Please enter a valid email address and choose a customer.";
  }
  return "Could not create the login. Please try again.";
}

// Per-row invite feedback, shown inline in the Actions cell (Session AFI). The colour is
// prescribed: success #63C41F (ele-green), rate-limit #b8860b (amber), error #c0392b (red).
type InviteFeedback = { kind: "success" | "rate" | "error"; text: string };

// The ink for each feedback kind comes from the shared tone helper: green success, amber rate limit
// (the theme-mapped amber, which passes AA on both themes), red error (the app safety token, which
// reads on the dark background where the document-palette #c0392b does not).
function feedbackClass(kind: InviteFeedback["kind"]): string {
  return toneTextClass(kind === "success" ? "green" : kind === "rate" ? "amber" : "red");
}

export function PortalUsersPage() {
  const [users, setUsers] = useState<PortalAdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Invite (send / re-send the sign-in link). Available to the same office+admin staff who create
  // and disable logins (Session AFI widened the server invite route to match, so the office manager
  // sees a working button on every row). A per-row 60-second freeze after a send guards against an
  // accidental double-send; feedback is shown inline on the row, not as a page-top banner.
  // In-flight sends are tracked PER ROW (not a single shared id) so two interleaved sends never
  // cross state: each row's busy/disabled test keys off its own id (Session AFI review).
  const [invitingIds, setInvitingIds] = useState<Record<string, boolean>>({});
  const [frozenIds, setFrozenIds] = useState<Record<string, number>>({});
  const [rowFeedback, setRowFeedback] = useState<Record<string, InviteFeedback>>({});

  function clearRowFeedback(id: string) {
    setRowFeedback((f) => {
      if (!(id in f)) return f;
      const next = { ...f };
      delete next[id];
      return next;
    });
  }

  const [email, setEmail] = useState("");
  const [customerId, setCustomerId] = useState("");
  // The picked customer's name, kept from the picker so the no-active-contract confirm can name them (Session PU1).
  const [customerLabel, setCustomerLabel] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism, the
  // InvoicesPage precedent). jsdom has no matchMedia, so every existing test still renders the
  // table it pins.
  const [mobileCards] = useState(initialMobileLayout);
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("portalUsers");

  // After a successful create, the confirmation banner offers a one-click "Send invite now" so the
  // office can create a login and email its sign-in link in a single step (Session AFI).
  const [createdBanner, setCreatedBanner] = useState<{ id: string; email: string; customerName: string } | null>(null);
  // Session PU1: the server refuses a login or invite for a customer with NO ACTIVE contract unless the office confirms it.
  // The refused action waits here behind the branded confirm naming the customer and the recipient; "run" repeats the SAME
  // request with the confirmation. Nothing is remembered: the next request asks the server again.
  const [pendingConfirm, setPendingConfirm] = useState<{
    kind: "create" | "invite";
    customerName: string;
    recipient: string;
    run: () => Promise<void>;
  } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [bannerInvite, setBannerInvite] = useState<"idle" | "sending" | "sent" | "error">("idle");

  function freezeRow(id: string) {
    setFrozenIds((f) => ({ ...f, [id]: Date.now() }));
    // Auto-clear the 60-second freeze so the button becomes available again.
    window.setTimeout(() => {
      setFrozenIds((f) => {
        const next = { ...f };
        delete next[id];
        return next;
      });
    }, 60_000);
  }

  async function sendInvite(u: PortalAdminUser, confirmNoActiveContract = false) {
    setInvitingIds((s) => ({ ...s, [u.id]: true }));
    clearRowFeedback(u.id); // clear any prior feedback on this row before the new attempt
    try {
      await (confirmNoActiveContract ? api.portalAdmin.sendInvite(u.id, { confirmNoActiveContract: true }) : api.portalAdmin.sendInvite(u.id));
      freezeRow(u.id);
      setRowFeedback((f) => ({ ...f, [u.id]: { kind: "success", text: `Sent to ${u.email}` } }));
      // The green "Sent to ..." note fades after 3 seconds; the button stays "Sent" for the full
      // 60-second freeze above. Do not clobber a newer message that may have replaced it.
      window.setTimeout(() => {
        setRowFeedback((f) => {
          if (f[u.id]?.kind !== "success") return f;
          const next = { ...f };
          delete next[u.id];
          return next;
        });
      }, 3_000);
    } catch (err) {
      if (isNoActiveContractRefusal(err)) {
        setPendingConfirm({ kind: "invite", customerName: customerName(u), recipient: u.email, run: () => sendInvite(u, true) });
      } else if (err instanceof ApiError && err.status === 429) {
        setRowFeedback((f) => ({ ...f, [u.id]: { kind: "rate", text: "Already sent recently" } }));
      } else {
        setRowFeedback((f) => ({ ...f, [u.id]: { kind: "error", text: "Failed - try again" } }));
      }
    } finally {
      setInvitingIds((s) => {
        const next = { ...s };
        delete next[u.id];
        return next;
      });
    }
  }

  // The banner's "Send invite now" (Session AFI): same invite call as the row button, for the login
  // just created. On success the banner settles on "Invite sent" and the matching table row freezes.
  async function sendBannerInvite(created: { id: string; email: string; customerName: string }, confirmNoActiveContract = false) {
    setBannerInvite("sending");
    try {
      await (confirmNoActiveContract
        ? api.portalAdmin.sendInvite(created.id, { confirmNoActiveContract: true })
        : api.portalAdmin.sendInvite(created.id));
      setBannerInvite("sent");
      freezeRow(created.id);
    } catch (err) {
      if (isNoActiveContractRefusal(err)) {
        setBannerInvite("idle");
        setPendingConfirm({
          kind: "invite",
          customerName: created.customerName,
          recipient: created.email,
          run: () => sendBannerInvite(created, true),
        });
      } else {
        setBannerInvite("error");
      }
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setUsers(await api.portalAdmin.listUsers());
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The customer's name as the server sent it with the login (an archived or one-off customer still reads by name).
  const customerName = (u: PortalAdminUser): string => u.customerName ?? "Unknown customer";

  async function submit(e: FormEvent) {
    e.preventDefault();
    await createLogin(false);
  }

  async function createLogin(confirmNoActiveContract: boolean) {
    if (saving) return; // block Enter-key re-entry while a create is in flight
    setFormError(null);
    setCreatedBanner(null);
    setBannerInvite("idle");
    if (!email.trim()) {
      setFormError("Please enter an email address.");
      return;
    }
    if (!customerId) {
      setFormError("Please choose a customer.");
      return;
    }
    setSaving(true);
    try {
      const created = await api.portalAdmin.createUser({
        email: email.trim(),
        customerId,
        name: name.trim() || undefined,
        ...(confirmNoActiveContract ? { confirmNoActiveContract: true } : {}),
      });
      setCreatedBanner({ id: created.id, email: created.email, customerName: customerLabel || "this customer" });
      setBannerInvite("idle");
      setEmail("");
      setCustomerId("");
      setCustomerLabel("");
      setName("");
      await load();
    } catch (err) {
      if (isNoActiveContractRefusal(err)) {
        // The form keeps what was typed; the confirm repeats this same create with the office's yes.
        setPendingConfirm({ kind: "create", customerName: customerLabel || "this customer", recipient: email.trim(), run: () => createLogin(true) });
      } else {
        setFormError(createErrorMessage(err));
      }
    } finally {
      setSaving(false);
    }
  }

  // Disable (revoke access) or re-enable a portal login. A PortalUser is never hard-deleted (it
  // is kept for the audit trail and is a Customer-delete blocker), so disable IS the "remove
  // access" control (Session AB, Item 6). Available to office + admin, matching the create-login
  // gate and the server endpoint's guard (the same staff who create logins can disable them).
  async function toggleDisabled(u: PortalAdminUser) {
    const disabling = u.disabledAt == null;
    setTogglingId(u.id);
    setCreatedBanner(null);
    setToggleError(null);
    // Clear any lingering invite feedback for this row: once disabled its Send button is hidden, so
    // a stale "Failed" note would otherwise sit under a row with no control to clear it (AFI review).
    clearRowFeedback(u.id);
    try {
      await api.portalAdmin.setDisabled(u.id, disabling);
      await load();
    } catch {
      setToggleError(`Could not update the login for ${u.email}. Please try again.`);
    } finally {
      setTogglingId(null);
    }
  }

  const visible = useMemo(() => {
    const searchAccessors: SearchAccessors<PortalAdminUser> = [(u) => customerName(u), (u) => u.email, (u) => u.name];
    const sortAccessors: SortAccessors<PortalAdminUser> = {
      customer: (u) => customerName(u).toLowerCase(),
      email: (u) => u.email.toLowerCase(),
      name: (u) => (u.name ?? "").trim().toLowerCase(),
      lastSignIn: (u) => u.lastLoginAt ?? "",
      status: (u) => (u.disabledAt ? "disabled" : "active"),
      created: (u) => u.createdAt,
    };
    return sortRows(filterRows(users, search, searchAccessors), sortAccessors, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, search, sort.sortKey, sort.sortDir]);

  // Card-only actions (Session ALY mobile card conversion). Same send-invite/toggle logic and
  // copy as the desktop cell below; only the button treatment differs (bordered + a 44px tap
  // target instead of the desktop's underline text link, per MobileListCard's "actions must carry
  // their own tap target" rule).
  function portalUserCardActions(u: PortalAdminUser) {
    const fb = rowFeedback[u.id];
    return (
      <>
        {!u.disabledAt && (
          <button
            type="button"
            onClick={() => sendInvite(u)}
            disabled={Boolean(invitingIds[u.id]) || Boolean(frozenIds[u.id])}
            title={`Email a one-time verification code to ${u.email}.`}
            className="min-h-11 flex-1 rounded-ele border border-ele-loler px-2 py-1 font-menu text-xs tracking-wide text-ele-loler transition hover:bg-ele-inset disabled:opacity-50"
          >
            {invitingIds[u.id] ? (
              "Sending..."
            ) : frozenIds[u.id] ? (
              <>
                Sent <span aria-hidden="true">✓</span>
              </>
            ) : u.lastLoginAt ? (
              "Send portal link"
            ) : (
              "Send invite"
            )}
          </button>
        )}
        {u.disabledAt ? (
          <Button variant="secondary" size="sm" onClick={() => toggleDisabled(u)} disabled={togglingId === u.id}>
            {togglingId === u.id ? "Saving..." : "Enable"}
          </Button>
        ) : (
          <ConfirmButton
            onConfirm={() => toggleDisabled(u)}
            label="Disable"
            busy={togglingId === u.id}
            busyLabel="Saving..."
            title={`Disable the Client Portal login for ${u.email}. They lose access to the Client Portal immediately. You can re-enable it later.`}
            className="min-h-11 rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-danger transition hover:bg-ele-inset disabled:opacity-50"
          />
        )}
        {!u.disabledAt && fb && (
          <p role={fb.kind === "success" ? "status" : "alert"} className={`w-full break-words text-xs ${feedbackClass(fb.kind)}`}>
            {fb.text}
          </p>
        )}
      </>
    );
  }

  async function confirmPending() {
    if (!pendingConfirm) return;
    const { run } = pendingConfirm;
    setConfirmBusy(true);
    try {
      await run();
    } finally {
      setConfirmBusy(false);
      setPendingConfirm(null);
    }
  }

  return (
    <div>
      {pendingConfirm && (
        <NoActiveContractConfirm
          kind={pendingConfirm.kind}
          customerName={pendingConfirm.customerName}
          recipient={pendingConfirm.recipient}
          busy={confirmBusy}
          onConfirm={() => void confirmPending()}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
      <PageHeader
        className="mb-4"
        title="Client Portal logins"
        subtitle="Customer sign-ins for the self-service Client Portal. Each login is tied to one customer and signs in with a 6-digit verification code sent to their email. There are no passwords to manage."
      />

      {/* Create form */}
      <form onSubmit={submit} className="mb-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <h2 className="mb-4 font-heading text-xl text-ele-blue">New Client Portal login</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm" htmlFor="portal-email">
              Email address<span className="text-ele-green"> *</span>
            </label>
            <input
              id="portal-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="name@company.co.uk"
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm" htmlFor="portal-customer">
              Customer<span className="text-ele-green"> *</span>
            </label>
            <RecordPicker kind="customer" id="portal-customer" value={customerId} onChange={(id, rec) => { setCustomerId(id); setCustomerLabel(rec?.label ?? ""); }} ariaLabel="Customer" placeholder="Search for a customer" required />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm" htmlFor="portal-name">
              Contact name
            </label>
            <input
              id="portal-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Optional"
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
            />
            <p className="mt-1 text-xs text-ele-body">Who this login is for. Optional.</p>
          </div>
        </div>


        {formError && (
          <p
            role="alert"
            className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong"
          >
            {formError}
          </p>
        )}

        <div className="mt-5">
          <Button type="submit" disabled={saving}>
            {saving ? "Creating..." : "Create login"}
          </Button>
        </div>
      </form>

      {/* Creation confirmation banner with a one-step "Send invite now" (Session AFI). */}
      {createdBanner && (
        <div
          role="status"
          className="mb-4 rounded-ele border border-ele-green bg-ele-surface px-3 py-3 text-sm text-ele-strong"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>Client Portal login created for {createdBanner.email}.</span>
            {bannerInvite === "sent" ? (
              <span className="font-menu text-xs uppercase tracking-wide text-ele-green">
                Invite sent <span aria-hidden="true">✓</span>
              </span>
            ) : (
              <Button size="sm" onClick={() => sendBannerInvite(createdBanner)} disabled={bannerInvite === "sending"}>
                {bannerInvite === "sending" ? "Sending..." : "Send invite now"}
              </Button>
            )}
          </div>
          {bannerInvite === "error" && (
            <p role="alert" className="mt-2 text-ele-danger">
              Failed to send. Use the Send invite button in the table below.
            </p>
          )}
        </div>
      )}

      {toggleError && (
        <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {toggleError}
        </p>
      )}

      {/* List */}
      {!loading && !loadError && users.length > 0 && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search customer, email or name..."
          searchLabel="Search Client Portal logins"
          resultCount={{ shown: visible.length, total: users.length, noun: "logins" }}
          sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={() => setSearch("")}
        />
      )}

      {loading ? (
        <Loader label="Loading the Client Portal logins..." />
      ) : loadError ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the Client Portal logins. Please try again in a moment.
        </p>
      ) : users.length === 0 ? (
        <EmptyState message="No Client Portal logins yet. Use the form above to create the first one." />
      ) : visible.length === 0 ? (
        <EmptyState role="status" message={`No Client Portal logins match "${search.trim()}".`} />
      ) : mobileCards ? (
        // Session ALY mobile card conversion: at 375px the table's surviving Customer + Status +
        // Actions columns still divide one phone width (and Actions carries two controls). The
        // card stacks instead: Email comes back as a second identifying line, Last sign-in as a
        // date line, and Name + Created (still not critical enough for the three main lines) sit
        // in the details block underneath.
        <MobileCardList label="Client Portal logins">
          {visible.map((u) => (
            <MobileListCard
              key={u.id}
              primary={customerName(u)}
              secondary={u.email}
              meta={`Last sign-in ${lastSignIn(u.lastLoginAt)}`}
              right={
                u.disabledAt ? <StatusPill word="Disabled" tone="red" /> : <StatusPill word="Active" tone="green" />
              }
              details={
                <dl className="space-y-1 text-xs">
                  <div className="flex justify-between gap-3">
                    <dt className="text-ele-body">Name</dt>
                    <dd className="text-ele-strong">{u.name?.trim() ? u.name : "None"}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-ele-body">Created</dt>
                    <dd className="text-ele-body">{formatDate(u.createdAt)}</dd>
                  </div>
                </dl>
              }
              actions={portalUserCardActions(u)}
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            {/* No detail page: below md the budget is Customer (identifier) + Status + the
                Send invite + Enable/Disable actions. Email, Name, Last sign-in and Created drop on
                mobile. Actions carries two controls, so it is widened to keep the buttons inside
                their own column at laptop widths (.cell-actions is overflow-visible, so a
                too-narrow column would paint over the neighbour). */}
            <colgroup>
              <col style={{ width: "20%" }} />
              <col className="max-md:hidden" style={{ width: "18%" }} />
              <col className="max-md:hidden" style={{ width: "13%" }} />
              <col className="max-md:hidden" style={{ width: "15%" }} />
              <col style={{ width: "9%" }} />
              <col className="max-md:hidden" style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Customer" colKey="customer" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Email" colKey="email" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Name" colKey="name" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Last sign-in" colKey="lastSignIn" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Created" colKey="created" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <th className="cell-actions px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u) => {
                const fb = rowFeedback[u.id];
                return (
                  <tr key={u.id} className="border-t border-ele-purple-accent">
                    <td className="px-3 py-2 align-top text-ele-strong">{customerName(u)}</td>
                    <td className="max-md:hidden px-3 py-2 align-top">{u.email}</td>
                    <td className="max-md:hidden px-3 py-2 align-top">
                      {u.name?.trim() ? u.name : <span className="text-ele-body">None</span>}
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top">{lastSignIn(u.lastLoginAt)}</td>
                    <td className="px-3 py-2 align-top">
                      {u.disabledAt ? <StatusPill word="Disabled" tone="red" /> : <StatusPill word="Active" tone="green" />}
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top text-ele-body">{formatDate(u.createdAt)}</td>
                    <td className="cell-actions px-3 py-2 text-right align-top">
                      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                        {/* Send / re-send the one-time sign-in invite (hidden only for a disabled
                            login). Blue outline button (#2CB9F3). Label follows whether the customer
                            has ever signed in; during the 60s cooldown it reads "Sent". */}
                        {!u.disabledAt && (
                          <button
                            type="button"
                            onClick={() => sendInvite(u)}
                            disabled={Boolean(invitingIds[u.id]) || Boolean(frozenIds[u.id])}
                            title={`Email a one-time verification code to ${u.email}.`}
                            className="rounded-ele border border-ele-loler px-2 py-1 font-menu text-xs tracking-wide text-ele-loler transition hover:bg-ele-inset disabled:opacity-50"
                          >
                            {invitingIds[u.id] ? (
                              "Sending..."
                            ) : frozenIds[u.id] ? (
                              <>
                                Sent <span aria-hidden="true">✓</span>
                              </>
                            ) : u.lastLoginAt ? (
                              "Send portal link"
                            ) : (
                              "Send invite"
                            )}
                          </button>
                        )}
                        {u.disabledAt ? (
                          <button
                            type="button"
                            onClick={() => toggleDisabled(u)}
                            disabled={togglingId === u.id}
                            className="font-menu text-xs tracking-wide text-ele-green transition hover:underline disabled:opacity-50"
                          >
                            {togglingId === u.id ? "Saving..." : "Enable"}
                          </button>
                        ) : (
                          <ConfirmButton
                            onConfirm={() => toggleDisabled(u)}
                            label="Disable"
                            busy={togglingId === u.id}
                            busyLabel="Saving..."
                            title={`Disable the Client Portal login for ${u.email}. They lose access to the Client Portal immediately. You can re-enable it later.`}
                            className="font-menu text-xs tracking-wide text-ele-danger transition hover:underline disabled:opacity-50"
                          />
                        )}
                      </div>
                      {!u.disabledAt && fb && (
                        <p role={fb.kind === "success" ? "status" : "alert"} className={`mt-1 break-words text-xs ${feedbackClass(fb.kind)}`}>
                          {fb.text}
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
